<template>
    <div>
        <div class="chart-title is-pulled-left">
            <h1 class="title">
                {{title}}
            </h1>
            <h2 class="subtitle">{{subtitle}}</h2>
        </div>
        <div class="chart-toolbar is-pulled-right level">
            <b-select class="is-pulled-left" size="is-small" v-model="selectedDisplayPeriod" v-if="availableDisplayPeriods.length">
                <option v-for="displayPeriod in availableDisplayPeriods" :value="displayPeriod" :key="displayPeriod.key">
                    {{ displayPeriod.value }}
                </option>
            </b-select>

            <button class="is-pulled-right button is-small" @click="exportPPTX" :disabled="isExporting || !project.metrics.length">
                <b-icon icon="file-powerpoint"
                        size="is-small"
                        pack="far">
                </b-icon>
                <span>Export</span>
            </button>
        </div>
        <b-table :data="scores"
                :checked-rows.sync="visibleBrands"
                @check="updateVisibleBrands"
                checkable>
            <template slot-scope="props">
                <b-table-column field="colour" label="" width ="30">
                    <verte v-model="columnsById[props.row.columnId].colour" menuPosition="center" :enableAlpha="false" model="hex"></verte>
                </b-table-column>
                <b-table-column custom-key="brandName" field="brandName" label="Brand Name" width="40" sortable>
                    {{ props.row.brandName }}
                </b-table-column>
                <b-table-column field="finalScore" label="Conversability Score" width="40" sortable numeric class="has-text-weight-semibold">
                    {{ props.row.finalScore.toFixed(2) }}
                    <metric-indicator
                        :value="maxMetrics.finalScore ? props.row.finalScore.toFixed(2) / maxMetrics.finalScore : 0">
                    </metric-indicator>
                </b-table-column>
                <b-table-column field="xConversation" label="Conversation" width="40" sortable numeric>
                    {{ props.row.xConversation.toFixed(2) }}
                    <metric-indicator
                        :value="maxMetrics.xConversation ? props.row.xConversation.toFixed(2) / maxMetrics.xConversation : 0">
                    </metric-indicator>
                </b-table-column>
                <b-table-column field="yQuality" label="Quality" width="40" sortable numeric>
                    {{ props.row.yQuality.toFixed(2) }}
                    <metric-indicator
                        :value="maxMetrics.yQuality ? props.row.yQuality.toFixed(2) / maxMetrics.yQuality : 0">
                    </metric-indicator>
                </b-table-column>
                <b-table-column field="zCommunity" label="Community" width="40" sortable numeric>
                    {{ props.row.zCommunity.toFixed(2) }}
                    <metric-indicator
                        :value="maxMetrics.zCommunity ? props.row.zCommunity.toFixed(2) / maxMetrics.zCommunity : 0">
                    </metric-indicator>
                </b-table-column>
            </template>
        </b-table>
    </div>
</template>

<script>
import _ from 'lodash';
import MetricIndicator from '@/components/MetricIndicator';
import { dateHelperService } from '@/services/date-helper-service';
import { chartLabelGeneratorService } from '@/services/chart-label-generator-service';
import { snapshotChartHelperService } from '@/services/snapshot-chart-helper-service';
import { exporterService } from '@/services/exporter-service';
import { finalScoresTableRequestor } from '@/requestors/final-scores-table-requestor.js';
import constants from '@/constants.js';

export default {
    name: 'FinalScoreTable',
    components: {
        MetricIndicator
    },
    props: {
        project: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            title: 'Conversability Scorecard',
            subtitle: '',
            scores: [],
            visibleBrands: [],
            maxMetrics: {},
            columnsById: {},
            errorMessage: '',
            isVisibleBrandsInitialised: false,
            availableDisplayPeriods: [],
            selectedDisplayPeriod: '',
            isExporting: false
        };
    },
    methods: {
        getTooltip: function (value) {
            if (value === 0) return 'No Change';

            let earliestMetric = _.minBy(this.project.metrics, m => m.day);
            let tooltip = ((value > 0) ? '+' : '') + value + '% since ' + earliestMetric.periodName;
            return tooltip;
        },
        getIcon: function (value) {
            return value < 0 ? 'caret-down' : value > 0 ? 'caret-up' : '';
        },
        updateVisibleBrands (checkedList) {
            this.project.settings.columns.forEach(column => {
                column.enabled = !!checkedList.find(el => el.columnId === column.id);
            });
        },
        async exportPPTX () {
            this.isExporting = true;
            let data = {
                projectEndDate: dateHelperService.customFormat(this.project.utcDateRange.end, 'DD MMM YYYY'),
                projectPeriod: this.project.meta.period.name,
                scores: this.scores
            };
            let pptxRequest = finalScoresTableRequestor.generatePPTXRequest(data, this.title, this.subtitle, this.project.settings);

            try {
                await exporterService.exportPPTX(pptxRequest);
            } catch (error) {
                console.log(error);
                this.$buefy.toast.open({
                    message: 'Failed to export ' + this.title + ' Table',
                    type: 'is-danger'
                });
            } finally {
                this.isExporting = false;
            }
        },
        loadMetricsBasedOnSelectedPeriod () {
            let metrics = {};
            if (this.selectedDisplayPeriod.key === snapshotChartHelperService.AVG_METRICS_KEY) {
                metrics = this.project.averageMetricsForEntireRange;
                this.subtitle = this.selectedDisplayPeriod.value;
            } else {
                metrics = this.project.metrics.filter(m => m.periodName === this.selectedDisplayPeriod.key);

                if (metrics && metrics.length > 0) {
                    let strPattern = chartLabelGeneratorService.generateSnapChartLabelPattern(this.project.meta.period, this.project.meta.daySmoothing);
                    this.subtitle = (this.project.meta.period.key === constants.PERIOD.DAY || this.project.meta.period.key === constants.PERIOD.WEEK)
                        ? chartLabelGeneratorService.formatString(strPattern, metrics[0].periodStartDate, metrics[0].periodEndDate)
                        : chartLabelGeneratorService.formatString(strPattern, metrics[0].periodName);
                }
            }

            if (metrics.length > 0) {
                this.scores = _.sortBy(metrics, m => m.brandName.toLowerCase()); // Sort by brand name in ascending order
                if (!this.isVisibleBrandsInitialised) {
                    this.visibleBrands = [...this.scores];
                    this.isVisibleBrandsInitialised = true;
                } else {
                    this.visibleBrands = _.filter(this.scores, s => {
                        return _.find(this.visibleBrands, b => s.columnId === b.columnId);
                    });
                }
                this.updateVisibleBrands(this.visibleBrands);
                // normalize using max value = 100%
                this.maxMetrics = {
                    finalScore: _.maxBy(this.scores, m => m.finalScore).finalScore,
                    xConversation: _.maxBy(this.scores, m => m.xConversation).xConversation,
                    yQuality: _.maxBy(this.scores, m => m.yQuality).yQuality,
                    zCommunity: _.maxBy(this.scores, m => m.zCommunity).zCommunity
                };
            }
        }
    },
    mounted () {
        let chartDetail = {
            getSlideRequest: () => {
                let data = {
                    projectEndDate: dateHelperService.customFormat(this.project.utcDateRange.end, 'DD MMM YYYY'),
                    projectPeriod: this.project.meta.period.name,
                    scores: this.scores
                };
                return finalScoresTableRequestor.getSlideRequest(data, this.title, this.subtitle, this.project.settings);
            }
        };
        this.$emit('on-chart-mount-complete', chartDetail);
    },
    watch: {
        'project.metrics': function (newMetrics, oldMetrics) {
            this.availableDisplayPeriods = snapshotChartHelperService.buildAvailableDisplayPeriods(newMetrics, this.project.averageMetricsForEntireRange,
                this.project.meta.period, this.project.meta.daySmoothing);
            if (this.availableDisplayPeriods && this.availableDisplayPeriods.length > 0) {
                if (this.selectedDisplayPeriod === this.availableDisplayPeriods[0]) {
                    this.loadMetricsBasedOnSelectedPeriod();
                } else {
                    this.selectedDisplayPeriod = this.availableDisplayPeriods[0];
                }
            }
        },
        'project.settings.columns': function (newColumns) {
            this.columnsById = _.keyBy(newColumns, 'id');
        },
        selectedDisplayPeriod: function () {
            this.loadMetricsBasedOnSelectedPeriod();
        }
    }
};
</script>

<style lang="scss" scoped>
    .title {
        font-size: 16px;

        .tooltip-info {
            cursor: pointer;

            &:hover {
                opacity: 0.4;
            }
        }
    }
    .subtitle {
        font-size: 14px;
        margin-bottom: 5px;
    }
</style>
